/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { styles } from './themes/PictoChartStyles.css';
import { classNames } from '#utils/UNSAFE_classNames';
import { BundleType } from '#resources/nls/bundle';
import { getItemAriaProps } from './pictoChartUtils';
import type { RefObject } from 'preact';
import { PictoChartItemData } from './pictoChart.type';
import { useUser } from '#hooks/UNSAFE_useUser';

export type PictoElementProps<K, D extends PictoChartItemData<K>> = {
  item: D;
  children: ComponentChildren;
  row: number;
  column: number;
  totalCount: number;
  class?: string;
  activeId?: string;
  isHighlighted?: boolean;
  isSelected?: boolean;
  hasSelection?: boolean;
  itemIdx?: number;
  countIdx?: number;
  supportsSelection?: boolean;
  supportsDrill?: boolean;
  translations?: BundleType;
  focusedItemRef?: RefObject<HTMLDivElement>;
  fractionalStart?: number;
  fractionalEnd?: number;
  isVerticalLayout?: boolean;
  layoutOrigin?: string;
};

const PictoChartElement = <K, D extends PictoChartItemData<K>>(props: PictoElementProps<K, D>) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const isVert = props.isVerticalLayout;
  let start = props.fractionalStart ?? 0;
  let end = props.fractionalEnd ?? 1;
  const layoutOrigin = props.layoutOrigin;
  if (isVert) {
    if (layoutOrigin === 'bottomStart' || layoutOrigin === 'bottomEnd') {
      [start, end] = [1 - end, 1 - start];
    }  
  }
  else {
    if (layoutOrigin === 'bottomEnd' || layoutOrigin === 'topEnd') {
      [start, end] = [1 - end, 1 - start];
    }
  }
  const shouldClip = start !== 0 || end !== 1;
  const clipPolygon = shouldClip ?
  (isVert ? 
  `polygon(0% ${start * 100}%, 100% ${start * 100}%, 100% ${end * 100}%, 0% ${end * 100}%)`
  : isRtl
  ? `polygon(${100 - end * 100}% 0%, ${100 - start * 100}% 0%, ${100 - start * 100}% 100%, ${100 - end * 100}% 100%)`
  : `polygon(${start * 100}% 0%, ${end * 100}% 0%, ${end * 100}% 100%, ${start * 100}% 100%)`)
  : undefined;
  const isInteractive = props.supportsSelection || props.supportsDrill;
  const { dimmedItemStyle, interactiveItemStyle } = styles;
  const itemClasses = classNames([
    isInteractive && interactiveItemStyle,
    !props.isHighlighted && dimmedItemStyle
  ]);
  const isFirstElement = props.countIdx === 0;
  const itemAriaProps =
    (isFirstElement &&
      getItemAriaProps(
        props.item.count || 1,
        props.totalCount,
        !!props.supportsSelection,
        props.item.label,
        props.translations,
        props.isSelected,
        props.hasSelection,
        props.item.accessibleLabel,
        props.activeId
      )) ||
    {};

  return (
    <div
      ref={isFirstElement ? props.focusedItemRef : undefined}
      id={props.activeId}
      data-idx={props.itemIdx}
      data-count-idx={props.countIdx}
      style={{
        gridRow: `${props.row + 1} / span ${props.item.rowSpan || 1}`,
        gridColumn: `${props.column + 1} / span ${props.item.columnSpan || 1}`,
        clipPath: clipPolygon
      }}
      class={itemClasses}
      {...itemAriaProps}>
      {props.children}
    </div>
  );
};

export { PictoChartElement };
